import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Seo from "../components/Seo";
import WhatsAppCta from "../components/fotografia/WhatsAppCta";
import FotoFaq from "../components/fotografia/FotoFaq";
import FondoEstudio from "../components/fotografia/FondoEstudio";
import FotoLeadRapido from "../components/fotografia/FotoLeadRapido";
import FotoPacks from "../components/fotografia/FotoPacks";
import FotoMosaico from "../components/fotografia/FotoMosaico";
import FotoComentarios from "../components/fotografia/FotoComentarios";
import FotoCampana from "../components/fotografia/FotoCampana";
import FotoCarrusel from "../components/fotografia/FotoCarrusel";
import FotoHeroEventos from "../components/fotografia/FotoHeroEventos";
import PhotoImage from "../components/fotografia/PhotoImage";
import { ESTUDIO } from "../data/estudio";
import { CLIENTES, hayClientes } from "../data/clientes";
import { CAMPANA, campanaActiva } from "../data/campana";
import { FAQS_HOME } from "../data/faqs";
import { FOTO_HERO, MOMENTOS } from "../data/momentos";
import { fotosDe } from "../data/portafolio";
import { PRECIOS, formatCLP } from "../data/precios";
import { segmentoPorId } from "../data/segmentos";
import { PAGES, breadcrumbJsonLd, faqJsonLd, organizationJsonLd } from "../seo/config";
import { SIZES } from "../utils/photos";

/*
  El home es de eventos y de nada más. Las fotos en estudio siguen existiendo
  como página y en la navegación, pero aquí no compiten: quien llega al inicio
  ve lo que más se contrata — revelación de sexo, embarazo, baby shower y boda.
*/
const eventos = segmentoPorId("eventos");
/* Bodas y eventos intercalados, para que la galería no sea dos bloques. */
const deEventos = fotosDe("eventos").filter((foto) => foto.id !== FOTO_HERO?.id);
const deBodas = fotosDe("bodas");
const galeria = Array.from({ length: Math.max(deEventos.length, deBodas.length) })
  .flatMap((_, i) => [deBodas[i], deEventos[i]])
  .filter(Boolean)
  .slice(0, 12);
const faqsScan = FAQS_HOME.slice(0, 4);

const HomePage = () => (
  <>
    <Seo
      title={PAGES.home.title}
      description={PAGES.home.description}
      keywords={PAGES.home.keywords}
      path="/"
      jsonLd={[
        organizationJsonLd(),
        breadcrumbJsonLd([{ name: "Inicio", path: "/" }]),
        faqJsonLd(FAQS_HOME),
      ]}
    />

    {/*
      LOS DOS BANNERS, EN CARRUSEL, CON LA CAMPAÑA PRIMERO. La mini sesión
      tiene fecha de término y es lo que se está vendiendo ahora, así que es la
      que abre. Al vencer desaparece sola y el de eventos queda solo — ahí el
      carrusel se dibuja sin controles, sin que haya que tocar nada.

      EL H1 SE QUEDA EN EL DE EVENTOS aunque ahora vaya segundo. El titular de
      la página dice de qué es el sitio, y eso es fotografía de eventos todo el
      año: colgarlo de una campaña de temporada lo haría desaparecer el 6 de
      diciembre. Por eso la campaña lleva h2 y el orden del documento no
      coincide con la jerarquía de títulos.

      Las dos están siempre en el DOM, apiladas; el carrusel solo cambia cuál
      se ve. Ver FotoCarrusel.js: de eso dependen el SEO, que no haya salto de
      layout y que la foto de la que no se ve llegue descargada.
    */}
    <FotoCarrusel
      label="Banners del inicio"
      etiquetas={[CAMPANA.nombre, "Bodas y eventos"]}
    >
      {campanaActiva() ? <FotoCampana /> : null}
      <FotoHeroEventos />
    </FotoCarrusel>

    <section id="momentos" className="bg-sand-50 py-16 md:py-20">
      <div className="container-omega">
        <h2 className="section-title max-w-2xl">Lo que pasa una sola vez.</h2>
        <p className="section-copy mt-4 max-w-xl">
          Vamos a tu evento y nos quedamos las horas que dure. Tú lo vives; nosotros lo guardamos.
        </p>
        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {MOMENTOS.map((momento) => (
            <li
              key={momento.id}
              className="group relative flex aspect-[3/4] flex-col justify-end overflow-hidden rounded-2xl bg-navy-900 text-white"
            >
              {momento.foto ? (
                <PhotoImage
                  foto={momento.foto}
                  bn
                  sizes={SIZES.grid4}
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-105"
                />
              ) : (
                <FondoEstudio variante="lateral" />
              )}
              <div
                className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/20 to-navy-950/0"
                aria-hidden="true"
              />
              <div className="relative z-10 p-5">
                <h3 className="font-display text-2xl font-bold tracking-tight">{momento.titulo}</h3>
                <p className="mt-1 text-sm text-navy-100">{momento.frase}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <WhatsAppCta
                    message={momento.whatsapp}
                    pack={momento.titulo}
                    location={`home_momento_${momento.id}`}
                    className="!rounded-full !px-4 !py-2.5 !text-xs"
                  >
                    Reservar
                  </WhatsAppCta>
                  <Link
                    to={momento.to}
                    aria-label={`Ver fotos de ${momento.titulo.toLowerCase()}`}
                    className="btn-on-dark-outline !px-4 !py-2.5 !text-xs"
                  >
                    Ver fotos
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <FotoPacks segmento={eventos} id="packs" className="bg-white py-16 md:py-20" />

    <section id="reserva-rapida" className="relative overflow-hidden bg-navy-900 py-16 text-white md:py-20">
      <FondoEstudio variante="rejilla" />
      <div className="container-omega relative z-10 grid items-center gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-14">
        <div>
          <h2 className="font-display text-display-sm font-bold">¿No sabes cuál elegir?</h2>
          <p className="mt-4 max-w-md text-lg leading-relaxed text-navy-100">
            Cuéntanos qué evento es y cuánto dura. Te decimos qué pack te conviene y si tenemos la
            fecha.
          </p>
          <ul className="mt-7 space-y-3">
            {["Precio cerrado, sin sorpresas", "Santiago y Región Metropolitana", "Factura"].map(
              (item) => (
                <li key={item} className="flex items-start gap-2.5 text-navy-100">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-sand-400" aria-hidden="true" />
                  {item}
                </li>
              )
            )}
          </ul>
          <p className="mt-6 text-sm text-navy-300">
            ¿Te casas?{" "}
            <Link to="/fotografo-de-bodas" className="font-semibold text-sand-200 underline underline-offset-4">
              Ver packs de boda
            </Link>
          </p>
        </div>
        <FotoLeadRapido />
      </div>
    </section>

    {galeria.length ? (
      <section id="galeria" className="bg-navy-950 py-16 text-white md:py-20">
        <div className="container-omega">
          <h2 className="font-display text-display-sm font-bold">Así quedan guardados.</h2>
          <FotoMosaico fotos={galeria} className="mt-10" />
          <p className="mt-8">
            <Link to="/portafolio" className="text-sm font-semibold text-sand-200 hover:text-white">
              Ver portafolio
            </Link>
          </p>
        </div>
      </section>
    ) : null}

    <FotoComentarios />

    <section id="confianza" className="bg-white py-12 md:py-14">
      <div className="container-omega">
        <dl className="grid gap-8 border-t border-navy-100 pt-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-sm text-navy-500">Dónde</dt>
            <dd className="mt-1 font-display text-lg font-bold text-navy-900">En tu evento</dd>
          </div>
          <div>
            <dt className="text-sm text-navy-500">Entrega</dt>
            <dd className="mt-1 font-display text-lg font-bold text-navy-900">En {eventos.entrega}</dd>
          </div>
          <div>
            <dt className="text-sm text-navy-500">Cobertura</dt>
            <dd className="mt-1 font-display text-lg font-bold text-navy-900">Santiago y RM</dd>
          </div>
          <div>
            <dt className="text-sm text-navy-500">Pago</dt>
            <dd className="mt-1 font-display text-lg font-bold text-navy-900">
              {ESTUDIO.emiteFactura ? "Con factura" : "Con boleta"}
            </dd>
          </div>
        </dl>

        {hayClientes() ? (
          <p className="mt-8 text-sm text-navy-600">
            {CLIENTES.map((cliente, i) => (
              <span key={cliente.id}>
                {i > 0 ? " · " : null}
                {cliente.instagram ? (
                  <a
                    href={cliente.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-navy-900 underline decoration-sand-400 decoration-2 underline-offset-4"
                  >
                    {cliente.nombre}
                  </a>
                ) : (
                  <span className="font-semibold text-navy-900">{cliente.nombre}</span>
                )}
              </span>
            ))}
          </p>
        ) : null}
      </div>
    </section>

    <FotoFaq
      faqs={faqsScan}
      compact
      whatsapp="Hola Omega, tengo una duda antes de reservar mi evento."
      location="faq_home"
    />

    <section id="reservar" className="relative overflow-hidden bg-navy-900 py-16 text-white md:py-20">
      <FondoEstudio variante="lateral" />
      <div className="container-omega relative z-10">
        <h2 className="max-w-2xl font-display text-display-sm font-bold">
          Tu evento pasa una vez. Las fotos se quedan.
        </h2>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <WhatsAppCta message={eventos.whatsapp} location="home_final" className="w-full sm:w-auto">
            Reservar mi fecha
          </WhatsAppCta>
          <Link to={eventos.path} className="btn-on-dark-outline w-full sm:w-auto">
            Cómo trabajamos
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default HomePage;
